/**
 * CreateListModal Component
 *
 * Modal for creating or editing favorites lists.
 * Includes name input, color picker grid, and icon picker grid.
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../base/Modal';
import Button from '../base/Button';
import type { FavoriteList } from '../../hooks/useFavorites';

export interface CreateListModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (name: string, color: string, icon: string) => Promise<void>;
  editingList?: FavoriteList | null;
  colors: string[];
  icons: string[];
}

export default function CreateListModal({
  isOpen,
  onClose,
  onSubmit,
  editingList,
  colors,
  icons,
}: CreateListModalProps) {
  const { t, i18n } = useTranslation('favorites');
  const isRTL = i18n.dir() === 'rtl';

  const [name, setName] = useState('');
  const [selectedColor, setSelectedColor] = useState(colors[0] || '#004D40');
  const [selectedIcon, setSelectedIcon] = useState(icons[0] || 'ri-heart-line');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset form when modal opens/closes or editingList changes
  useEffect(() => {
    if (isOpen) {
      if (editingList) {
        setName(editingList.name);
        setSelectedColor(editingList.color || colors[0] || '#004D40');
        setSelectedIcon(editingList.icon || icons[0] || 'ri-heart-line');
      } else {
        setName('');
        setSelectedColor(colors[0] || '#004D40');
        setSelectedIcon(icons[0] || 'ri-heart-line');
      }
    }
  }, [isOpen, editingList, colors, icons]);

  const handleSubmit = async () => {
    if (!name.trim() || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await onSubmit(name.trim(), selectedColor, selectedIcon);
      onClose();
    } catch (error) {
      console.error('Failed to save list:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && name.trim() && !isSubmitting) {
      handleSubmit();
    }
  };

  const isEditing = !!editingList;
  const modalTitle = isEditing
    ? t('favorites.lists.editList')
    : t('favorites.lists.newList');

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={modalTitle}
      icon={isEditing ? 'ri-edit-line' : 'ri-add-line'}
      size="md"
    >
      <div className="space-y-5">
        {/* Name Input */}
        <div>
          <label
            htmlFor="list-name"
            className={`block text-sm font-medium text-gray-700 mb-2 ${isRTL ? 'text-right' : ''}`}
          >
            {t('favorites.lists.name')}
          </label>
          <input
            id="list-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('favorites.lists.createPlaceholder')}
            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#004D40] focus:border-transparent transition-all"
            autoFocus
            dir={isRTL ? 'rtl' : 'ltr'}
          />
        </div>

        {/* Color Selection */}
        <div>
          <label className={`block text-sm font-medium text-gray-700 mb-2 ${isRTL ? 'text-right' : ''}`}>
            {t('favorites.lists.color')}
          </label>
          <div className="flex flex-wrap gap-2">
            {colors.map((color) => (
              <button
                key={color}
                type="button"
                onClick={() => setSelectedColor(color)}
                className={`w-10 h-10 rounded-xl transition-all ${
                  selectedColor === color
                    ? 'ring-2 ring-offset-2 ring-[#004D40] scale-110'
                    : 'hover:scale-105'
                }`}
                style={{ backgroundColor: color }}
                aria-label={`Select color ${color}`}
                aria-pressed={selectedColor === color}
              />
            ))}
          </div>
        </div>

        {/* Icon Selection */}
        <div>
          <label className={`block text-sm font-medium text-gray-700 mb-2 ${isRTL ? 'text-right' : ''}`}>
            {t('favorites.lists.icon')}
          </label>
          <div className="flex flex-wrap gap-2">
            {icons.map((icon) => (
              <button
                key={icon}
                type="button"
                onClick={() => setSelectedIcon(icon)}
                className={`w-10 h-10 rounded-xl border flex items-center justify-center transition-all ${
                  selectedIcon === icon
                    ? 'border-[#004D40] bg-[var(--pv-color-surface-sunken)] text-[#004D40]'
                    : 'border-gray-200 text-gray-400 hover:border-[#1B7566] hover:text-[#004D40]'
                }`}
                aria-label={`Select icon ${icon}`}
                aria-pressed={selectedIcon === icon}
              >
                <i className={`${icon} text-xl`}></i>
              </button>
            ))}
          </div>
        </div>

        {/* Preview */}
        <div className={`p-4 bg-gray-50 rounded-xl ${isRTL ? 'text-right' : ''}`}>
          <p className="text-xs text-gray-500 mb-2">
            {isRTL ? 'معاينة' : 'Preview'}
          </p>
          <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: selectedColor }}
            >
              <i className={`${selectedIcon} text-xl text-white`}></i>
            </div>
            <span className="font-medium text-gray-900">
              {name || t('favorites.lists.createPlaceholder')}
            </span>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className={`flex gap-3 mt-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
        <Button
          variant="outline"
          onClick={onClose}
          className="flex-1"
          disabled={isSubmitting}
        >
          {t('favorites.actions.cancel')}
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={!name.trim() || isSubmitting}
          className="flex-1"
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center gap-2">
              <i className="ri-loader-4-line animate-spin"></i>
              {t('favorites.loading')}
            </span>
          ) : isEditing ? (
            t('favorites.actions.saveChanges')
          ) : (
            t('favorites.actions.create')
          )}
        </Button>
      </div>
    </Modal>
  );
}
